import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/utils/styles';

export enum Variant {
  /** font size: 16px, padding: 8px */
  Default = 'default',
  /** font size: 14px, padding: 6px */
  Small = 'small',
}

export interface EditableTextProps {
  initialValue: string;
  onSave: (newValue: string) => Promise<void>;
  variant?: Variant;
  inputClassName?: string;
  textClassName?: string;
  containerClassName?: string;
}

export const EditableText: React.FC<EditableTextProps> = ({
  initialValue,
  onSave,
  variant = Variant.Default,
  inputClassName = '',
  textClassName = '',
  containerClassName = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    // Reset value when initialValue changes
    setValue(initialValue);
  }, [initialValue]);

  const handleBlur = async () => {
    if (value !== initialValue) {
      try {
        await onSave(value);
      } catch (error) {
        setValue(initialValue);
      }
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setValue(initialValue);
    }
  };

  return (
    <div
      className={cn(
        {
          'text-sm': variant === Variant.Small,
          'text-base': variant === Variant.Default,
        },
        containerClassName,
      )}>
      {isEditing ? (
        <input
          type="text"
          ref={inputRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            'border border-neutral-700 rounded bg-neutral-900 w-full focus:outline-none',
            {
              'p-1.5': variant === Variant.Small,
              'p-2': variant === Variant.Default,
            },
            inputClassName,
          )}
        />
      ) : (
        <div
          onClick={() => setIsEditing(true)}
          className={cn(
            'cursor-pointer rounded hover:bg-neutral-900 border w-full border-transparent transition-colors',
            {
              'p-1.5': variant === Variant.Small,
              'p-2': variant === Variant.Default,
            },
            textClassName,
          )}>
          {value}
        </div>
      )}
    </div>
  );
};
